import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SupervisorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { getWebDashboardUsers, createWebDashboardUser, deactivateWebDashboardUser } from '../../services/user';

function UserLogs() {
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form states
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    role: 'user'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = await getWebDashboardUsers();
      setUsers(userData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please check your permissions.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Client-side validation
      if (!newUser.username || newUser.username.length < 3) {
        setError('Username must be at least 3 characters long');
        setLoading(false);
        return;
      }
      
      if (!newUser.password || newUser.password.length < 6) {
        setError('Password must be at least 6 characters long');
        setLoading(false);
        return;
      }
      
      await createWebDashboardUser(newUser.username, newUser.password, newUser.role);
      setSuccess('User created successfully');
      setCreateDialogOpen(false);
      setNewUser({ username: '', password: '', role: 'user' });
      
      // Refresh users list
      await fetchUsers();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error creating user:', err);
      
      // Handle error message properly - convert objects to strings
      let errorMessage = 'Failed to create user';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        
        // Check if detail is an array (Pydantic validation errors)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(error => {
            const field = error.loc?.join('.') || 'field';
            const msg = error.msg || 'validation error';
            return `${field}: ${msg}`;
          }).join(', ');
        } 
        // Check if detail is an object
        else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
        // Otherwise use as string
        else {
          errorMessage = detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await deactivateWebDashboardUser(selectedUser.username);
      setSuccess('User deactivated successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      
      // Refresh users list
      await fetchUsers();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deactivating user:', err);
      
      // Handle error message properly - convert objects to strings
      let errorMessage = 'Failed to deactivate user';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        
        // Check if detail is an array (Pydantic validation errors)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(error => {
            const field = error.loc?.join('.') || 'field';
            const msg = error.msg || 'validation error';
            return `${field}: ${msg}`;
          }).join(', ');
        } 
        // Check if detail is an object
        else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
        // Otherwise use as string
        else {
          errorMessage = detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const getUserIcon = (role) => {
    switch (role) {
      case 'admin':
        return <AdminIcon color="error" />;
      default:
        return <PersonIcon color="primary" />;
    }
  };

  const getRoleChip = (role) => {
    const roleConfig = {
      admin: { label: 'Admin', color: 'error', icon: <AdminIcon fontSize="small" /> },
      user: { label: 'User', color: 'primary', icon: <PersonIcon fontSize="small" /> }
    };

    const config = roleConfig[role] || roleConfig.user;
    
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleString() : 'Never';
  };

  return (
    <Grid container spacing={3}>
      {/* Header */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2}>
                <SupervisorIcon color="primary" fontSize="large" />
                <Box>
                  <Typography variant="h5" gutterBottom>
                    👥 WEB Dashboard User Logs
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Manage dashboard users and administrators. These users are excluded from social platform analytics.
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchUsers}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialogOpen(true)}
                  disabled={loading}
                >
                  Add User
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Alerts */}
      {error && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Grid>
      )}

      {success && (
        <Grid item xs={12}>
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Grid>
      )}

      {/* Loading */}
      {loading && (
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      )}

      {/* Users Table */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Dashboard Users ({Object.keys(users).length})
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(users).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No dashboard users found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    Object.entries(users).map(([username, user]) => (
                      <TableRow key={username} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getUserIcon(user.role)}
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {user.username}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ID: {user.user_id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {getRoleChip(user.role)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.is_active ? 'Active' : 'Inactive'}
                            color={user.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(user.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(user.last_login)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="error"
                            onClick={() => openDeleteDialog(user)}
                            disabled={loading || user.role === 'admin'}
                            title={user.role === 'admin' ? 'Cannot deactivate admin users' : 'Deactivate user'}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Dashboard User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Username"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={newUser.role}
                label="Role"
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateUser} 
            variant="contained"
            disabled={!newUser.username || !newUser.password || loading}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Deactivate User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate user "{selectedUser?.username}"? 
            This action will prevent them from logging into the dashboard.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained" disabled={loading}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default UserLogs;
